/**
 * contentProcessor.js - Utils
 * Funções utilitárias para processamento de conteúdo de artigos
 * Extraído do ArticleModal para reutilização
 */

/**
 * Detecta se o conteúdo contém HTML
 */
export const hasHTMLContent = (content) => {
  if (!content) return false;
  return content.includes('<') && content.includes('>') &&
    (content.includes('<img') || content.includes('<p>') || content.includes('<div>') || content.includes('<br>'));
};

/**
 * Intercala imagens no conteúdo respeitando a ordem original
 * Usado tanto no modal interno quanto na página pública
 */
export const processContentWithOriginalImages = (content, allImages) => {
  if (!content || !allImages || allImages.length === 0) {
    return content;
  }

  // Se o conteúdo já é HTML, verificar se tem imagens válidas
  if (hasHTMLContent(content)) {
    // Contar quantas tags <img> com src válidos existem no HTML
    const imgTags = content.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/gi) || [];
    const validImages = imgTags.filter(tag => {
      const srcMatch = tag.match(/src=["']([^"']+)["']/i);
      if (srcMatch) {
        const src = srcMatch[1];
        // Considerar inválidas: SVGs pequenos, ícones, placeholders
        return !src.includes('.svg') &&
          !src.includes('icon') &&
          !src.includes('logo') &&
          !src.includes('placeholder') &&
          src.startsWith('http');
      }
      return false;
    });

    // Se tem poucas imagens válidas (menos de 50% do allImages), injetar imagens
    if (validImages.length < allImages.length * 0.5) {
      console.log(`🔧 HTML tem poucas imagens válidas (${validImages.length}/${allImages.length}), injetando imagens do array`);

      // Dividir o HTML em seções usando DOMParser
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      const body = doc.body;

      // Percorrer os filhos diretos do body
      let imageIndex = 0;
      const newContent = [];

      body.childNodes.forEach((node, index) => {
        // Adicionar o nó original
        if (node.nodeType === 1) { // Element node
          newContent.push(node.outerHTML);
        } else if (node.nodeType === 3) { // Text node
          const text = node.textContent.trim();
          if (text) newContent.push(`<p>${text}</p>`);
        }

        // Decidir se deve inserir uma imagem após esta seção
        const shouldAddImage = (
          (index === 0 && imageIndex < allImages.length) || // Após primeira seção
          (index > 0 && index % 2 === 0 && imageIndex < allImages.length) || // A cada 2 seções
          (index === body.childNodes.length - 2 && imageIndex < allImages.length) // Penúltima seção
        );

        if (shouldAddImage) {
          newContent.push(`<div style="text-align: center; margin: 20px 0; max-width: 100%; box-sizing: border-box;"><img src="${allImages[imageIndex]}" alt="Imagem ${imageIndex + 1}" style="max-width: 100% !important; width: 100% !important; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); object-fit: contain; box-sizing: border-box; display: block; margin: 0 auto;" /></div>`);
          imageIndex++;
        }
      });

      // Adicionar imagens restantes no final
      while (imageIndex < allImages.length) {
        newContent.push(`<div style="text-align: center; margin: 20px 0; max-width: 100%; box-sizing: border-box;"><img src="${allImages[imageIndex]}" alt="Imagem ${imageIndex + 1}" style="max-width: 100% !important; width: 100% !important; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); object-fit: contain; box-sizing: border-box; display: block; margin: 0 auto;" /></div>`);
        imageIndex++;
      }

      return newContent.join('');
    }

    // HTML já tem imagens suficientes, retornar como está
    return content;
  }

  // Conteúdo é texto puro, processar como antes
  const paragraphs = content.split('\n').filter(p => p.trim());

  if (paragraphs.length === 0) {
    return content;
  }

  const result = [];
  let imageIndex = 0;

  // Estratégia de intercalação baseada na estrutura do artigo
  paragraphs.forEach((paragraph, index) => {
    // Adicionar o parágrafo
    result.push(`<p>${paragraph}</p>`);

    // Decidir onde inserir imagens baseado na posição e conteúdo
    const shouldAddImage = (
      // Após o primeiro parágrafo (introdução)
      (index === 0 && imageIndex < allImages.length) ||
      // Após parágrafos que mencionam características visuais
      (paragraph.toLowerCase().includes('visual') && imageIndex < allImages.length) ||
      (paragraph.toLowerCase().includes('design') && imageIndex < allImages.length) ||
      (paragraph.toLowerCase().includes('exterior') && imageIndex < allImages.length) ||
      (paragraph.toLowerCase().includes('interior') && imageIndex < allImages.length) ||
      // A cada 2-3 parágrafos para distribuição equilibrada
      (index > 0 && index % 3 === 0 && imageIndex < allImages.length) ||
      // Antes do último parágrafo
      (index === paragraphs.length - 2 && imageIndex < allImages.length)
    );

    if (shouldAddImage) {
      result.push(`<div style="text-align: center; margin: 20px 0; max-width: 100%; box-sizing: border-box;"><img src="${allImages[imageIndex]}" alt="Imagem ${imageIndex + 1}" style="max-width: 100% !important; width: 100% !important; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); object-fit: contain; box-sizing: border-box; display: block; margin: 0 auto;" /></div>`);
      imageIndex++;
    }
  });

  // Adicionar imagens restantes no final se houver
  while (imageIndex < allImages.length) {
    result.push(`<div style="text-align: center; margin: 20px 0; max-width: 100%; box-sizing: border-box;"><img src="${allImages[imageIndex]}" alt="Imagem ${imageIndex + 1}" style="max-width: 100% !important; width: 100% !important; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); object-fit: contain; box-sizing: border-box; display: block; margin: 0 auto;" /></div>`);
    imageIndex++;
  }

  return result.join('');
};

/**
 * Formata a data para exibição
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    // Usar horário LOCAL do browser (sem forçar UTC)
    // A data no banco é salva como meia-noite no horário de Brasília
    // Ex: 2026-03-29T21:00Z no MongoDB → 30/03/2026 no UTC-3 (correto)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};

/**
 * Decodifica entidades HTML
 */
export const decodeHtmlEntities = (text) => {
  if (!text) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = text;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Formata números com separadores de milhares
 */
export const formatNumber = (number) => {
  if (!number || number === 0) return '0';
  return number.toLocaleString('pt-BR');
};

/**
 * Retorna cor baseada no sentimento
 */
export const getSentimentColor = (sentiment) => {
  switch (sentiment) {
    case 'positive': return '#2196f3';  // Azul
    case 'negative': return '#f44336';  // Vermelho
    case 'neutral': default: return '#9e9e9e';  // Cinza
  }
};

/**
 * Traduz o sentimento para português
 */
export const translateSentiment = (sentiment) => {
  switch (sentiment) {
    case 'positive': return 'Positivo';
    case 'negative': return 'Negativo';
    case 'neutral': default: return 'Neutro';
  }
};

/**
 * Extrai o ID do YouTube de uma URL (incluindo Shorts)
 */
export const getYouTubeId = (url) => {
  if (!url) return null;

  try {
    const parsedUrl = new URL(url);

    // YouTube Shorts: /shorts/VIDEO_ID
    if (parsedUrl.pathname.includes('/shorts/')) {
      const match = parsedUrl.pathname.match(/\/shorts\/([^/?]+)/);
      return match ? match[1] : null;
    }

    // YouTube normal: ?v=VIDEO_ID ou &v=VIDEO_ID
    const videoId = parsedUrl.searchParams.get('v');
    if (videoId) return videoId;

    // YouTube Live: /live/VIDEO_ID
    if (parsedUrl.pathname.includes('/live/')) {
      const match = parsedUrl.pathname.match(/\/live\/([^/?]+)/);
      return match ? match[1] : null;
    }

    // youtu.be: /VIDEO_ID
    if (parsedUrl.hostname.includes('youtu.be')) {
      return parsedUrl.pathname.substring(1);
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Extrai tweetId da URL do Twitter/X
 */
export const getTweetId = (url) => {
  if (!url) return null;

  // Padrões de URL do Twitter/X
  const patterns = [
    /twitter\.com\/\w+\/status\/(\d+)/i,
    /x\.com\/\w+\/status\/(\d+)/i,
    /twitter\.com\/i\/status\/(\d+)/i,
    /x\.com\/i\/status\/(\d+)/i
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
};
